import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, Download, Trash2, X, Maximize2, Film, Play, Image as ImageIcon, User } from "lucide-react";


export default function ClipDetailView({ clip, onClose, onDelete, onDownload, api }) {
  const videoRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);
  const [activeFramePopup, setActiveFramePopup] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (activeFramePopup !== null && !e.target.closest('.clip-frame-thumb')) {
        setActiveFramePopup(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, activeFramePopup]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (clip?.id && clip?.status === 'completed') {
      const fetchVideo = async () => {
        try {
          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
          const token = localStorage.getItem('auth_token');
          const response = await fetch(`${baseUrl}/api/clip/${clip.id}/download`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setVideoSrc(url);
          }
        } catch (e) {
          console.error('Failed to load video:', e);
        }
      };
      fetchVideo();
      return () => {
        if (videoSrc) window.URL.revokeObjectURL(videoSrc);
      };
    }
  }, [clip?.id, clip?.status]);

  const handleDownload = async () => {
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${baseUrl}/api/clip/${clip.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${clip.song_title || 'clip'}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        window.alert('Download failed. Please try again.');
      }
    } catch (e) {
      console.error('Download error:', e);
      window.alert('Download failed: ' + e.message);
    }
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this clip? This cannot be undone.')) {
      onDelete && onDelete(clip.id);
    }
    setMenuOpen(false);
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!document.fullscreenElement) {
        videoRef.current.requestFullscreen().catch(err => {
          console.log('Fullscreen error:', err);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  const downloadFrameImage = (frame, idx) => {
    if (frame.image_base64) {
      const link = document.createElement('a');
      link.href = `data:image/jpeg;base64,${frame.image_base64}`;
      link.download = `${clip.song_title || 'clip'}_frame_${idx + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setActiveFramePopup(null);
  };

  const downloadFrameVideo = async (frame, idx) => {
    if (frame.video_url) {
      try {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${baseUrl}/api/clip/${clip.id}/frame/${idx}/video`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${clip.song_title || 'clip'}_frame_${idx + 1}.mp4`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          window.alert('Failed to download video');
        }
      } catch (e) {
        window.alert('Failed to download video: ' + e.message);
      }
    }
    setActiveFramePopup(null);
  };

  const thumbnail = clip?.frames?.find(f => f.image_base64)?.image_base64;
  const thumbnailUrl = thumbnail ? `data:image/jpeg;base64,${thumbnail}` : null;

  const totalFrames = clip?.frames?.length || 0;
  const videosReady = clip?.frames?.filter(f => f.video_url)?.length || 0;

  return (
    <div className="panel panel-detail panel-clip-detail" data-testid="clip-detail-view">
      <div className="panel-title-row">
        <h2 className="panel-title">{clip?.song_title || 'Clip'}</h2>
        <div className="panel-title-actions">
          <div className="menu-container" ref={menuRef}>
            <button
              className="btn-icon"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="clip-detail-menu-btn"
            >
              <MoreVertical size={20} />
            </button>
            {menuOpen && (
              <div className="dropdown-menu dropdown-menu-right">
                <button
                  className="dropdown-item"
                  onClick={handleDownload}
                  data-testid="clip-download-btn"
                >
                  <Download size={16} />
                  Download
                </button>
                <button
                  className="dropdown-item dropdown-item-danger"
                  onClick={handleDelete}
                  data-testid="clip-delete-menu-btn"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            )}
          </div>
          <button className="btn-icon" onClick={onClose} data-testid="close-clip-detail-btn">
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="video-player-container">
        {videoSrc && clip?.status === 'completed' ? (
          <>
            <video
              ref={videoRef}
              src={videoSrc}
              controls
              className="video-player"
              poster={thumbnailUrl}
              data-testid="clip-player"
            />
            <button
              className="video-fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              data-testid="clip-fullscreen-btn"
            >
              <Maximize2 size={18} />
            </button>
          </>
        ) : (
          <div className="video-player-placeholder">
            <Film size={48} />
            <span>{clip?.status === 'completed' ? 'Loading video...' : 'Clip not ready'}</span>
          </div>
        )}
      </div>

      <div className="clip-detail-info">
        <div className="clip-detail-meta">
          <div className="clip-detail-status">
            <span className="type-label type-clip">Clip</span>
            {clip?.status === 'completed' ? (
              <span className="text-accent">Ready to view</span>
            ) : (
              <span className="text-accent">{clip?.status?.replace(/_/g, ' ')}</span>
            )}
          </div>
          <div className="clip-detail-frames subtle">
            {totalFrames} frames • {videosReady} videos
          </div>
          {clip?.created_at && (
            <div className="clip-detail-date subtle">
              Created {new Date(clip.created_at).toLocaleDateString()}
            </div>
          )}
        </div>

        {clip?.frames && clip.frames.length > 0 && (
          <div className="clip-frames-preview">
            <div className="clip-frames-label subtle">Frames (click to download)</div>
            <div className="clip-frames-grid">
              {clip.frames.map((frame, idx) => (
                <div
                  key={idx}
                  className={`clip-frame-thumb ${activeFramePopup === idx ? 'active' : ''}`}
                  onClick={() => setActiveFramePopup(activeFramePopup === idx ? null : idx)}
                >
                  {frame.image_base64 ? (
                    <img src={`data:image/jpeg;base64,${frame.image_base64}`} alt={`Frame ${idx + 1}`} />
                  ) : (
                    <div className="clip-frame-empty">
                      <ImageIcon size={16} />
                    </div>
                  )}
                  {frame.with_actor && (
                    <div className="clip-frame-actor-badge" title="Actor scene">
                      <User size={10} />
                    </div>
                  )}
                  {activeFramePopup === idx && (
                    <div className="clip-frame-popup">
                      <button
                        className="clip-frame-popup-btn"
                        onClick={(e) => { e.stopPropagation(); downloadFrameImage(frame, idx); }}
                        title="Download image"
                        disabled={!frame.image_base64}
                      >
                        <ImageIcon size={14} />
                      </button>
                      <button
                        className="clip-frame-popup-btn"
                        onClick={(e) => { e.stopPropagation(); downloadFrameVideo(frame, idx); }}
                        title="Download video"
                        disabled={!frame.video_url}
                      >
                        <Play size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="clip-detail-actions">
        <button className="btn-primary" onClick={handleDownload} data-testid="clip-download-main-btn">
          <Download size={16} />
          Download Clip
        </button>
      </div>
    </div>
  );
}
